import { useLazyQuery } from "@apollo/client";
import { GET_ORDERS } from "./orders.query";
import { IGetOrdersResponse, IMappedOrder } from "./orders.interface";
import { mapOrdersResponse } from "./orders.mapper";
import { useEffect, useCallback, useState } from "react";
import { toastMessageActions } from "../../state/request";
import { useDispatch } from "react-redux";
import { DEFAULT_PAGE_SIZE } from "../../config/params";

export const useOrders = () => {
  const dispatch = useDispatch();
  const [allOrders, setAllOrders] = useState<IMappedOrder[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [ordersUrl, setOrdersUrl] = useState<string | undefined>();
  const [subscriptionsUrl, setSubscriptionsUrl] = useState<string | undefined>();
  const [hasInitiallyFetched, setHasInitiallyFetched] = useState(false);

  const [getOnlinePharmacyOrders, { loading, error }] = useLazyQuery<IGetOrdersResponse>(GET_ORDERS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const mapped = mapOrdersResponse(data);
      setAllOrders((prev) => [...prev, ...mapped.orders]);
      setHasMore(mapped.hasMore);
      setOrdersUrl(mapped.ordersUrl);
      setSubscriptionsUrl(mapped.subscriptionsUrl);
      setHasInitiallyFetched(true);
    },
  });

  useEffect(() => {
    if (error) {
      setHasInitiallyFetched(true);
      dispatch(
        toastMessageActions.setGlobalAlert({
          severity: "error",
          message: error.message,
        }),
      );
    }
  }, [error, dispatch]);

  const executeGetOrders = useCallback(() => {
    setAllOrders([]);
    setHasMore(false);
    setHasInitiallyFetched(false);
    getOnlinePharmacyOrders({
      variables: {
        limit: DEFAULT_PAGE_SIZE,
        page: 0,
      },
    });
  }, [getOnlinePharmacyOrders]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = Math.floor(allOrders.length / DEFAULT_PAGE_SIZE);
      getOnlinePharmacyOrders({
        variables: {
          limit: DEFAULT_PAGE_SIZE,
          page: nextPage,
        },
      });
    }
  }, [getOnlinePharmacyOrders, loading, hasMore, allOrders.length]);

  return {
    executeGetOrders,
    loadMore,
    data: {
      orders: allOrders,
      ordersUrl,
      subscriptionsUrl,
    },
    loading,
    hasMore,
    hasInitiallyFetched,
    error,
  };
};
